import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { MOBILE_APP_SCHEME } from '../config/Config';

const isMobileBrowser = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

const buildAppUrl = (scheme, token) => {
  const cleanScheme = (scheme || 'thinkstyne').replace(/:\/\/$/, '');
  return `${cleanScheme}://accept-invite?token=${encodeURIComponent(token)}`;
};

const MobileInviteLinkPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showFallback, setShowFallback] = useState(false);
  const token = searchParams.get('token') || '';

  const webInvitePath = useMemo(
    () => `/accept-invite${token ? `?token=${encodeURIComponent(token)}` : ''}`,
    [token]
  );

  useEffect(() => {
    if (!token) {
      navigate('/accept-invite', { replace: true });
      return undefined;
    }

    if (!isMobileBrowser()) {
      navigate(webInvitePath, { replace: true });
      return undefined;
    }

    let fallbackTimer;
    const openWebFallback = () => {
      if (document.visibilityState === 'visible') {
        setShowFallback(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        window.clearTimeout(fallbackTimer);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.location.href = buildAppUrl(MOBILE_APP_SCHEME, token);
    fallbackTimer = window.setTimeout(openWebFallback, 1400);

    return () => {
      window.clearTimeout(fallbackTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [navigate, token, webInvitePath]);

  const continueInBrowser = () => navigate(webInvitePath, { replace: true });

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" px={2}>
      <Card sx={{ maxWidth: 520, width: '100%' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {!showFallback ? (
            <>
              <CircularProgress sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Opening Thinkstyne
              </Typography>
              <Typography variant="body1" color="text.secondary">
                If the app is installed, it should open automatically.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>
                Continue in your browser
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                We could not open the app on this device. You can still accept the invitation here.
              </Typography>
              <Button variant="contained" onClick={continueInBrowser}>
                Open invitation
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MobileInviteLinkPage;
